"use client";

import { Gamja_Flower } from "next/font/google";
import { createPortal } from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { Card } from "@/components/ui/card";
import CategoryButtons from "@/components/CategoryButtons";
import { useIndexShell } from "@/components/article-shell/IndexShellContext";
import TheoryIntroClient from "./TheoryIntroClient";

const gamjaFlower = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

const bannerImageColumnClassName =
  "w-1/2 flex min-h-[216px] items-center justify-center p-2 md:min-h-[232px] md:p-4";
const bannerImageClassName = "h-auto w-[200px] max-w-full object-contain";

/**
 * `/theory` 인덱스 콘텐츠 (JCG-2097).
 *
 * 데스크탑(IndexShell 내부) → 카드를 좌/우 사이드바로 분배:
 *  - 상단 고정 바(headerSlot): 페이지 타이틀
 *  - 가운데(children): 공부법 인트로
 *  - 좌측 사이드바: 과목별 진입(CategoryButtons sidebar 변형)
 *  - 우측 사이드바: 랜덤 퀴즈 · 압축 공부법 · 요약 PDF · 4주 계획(컴팩트 배너)
 * 모바일/태블릿(셸 밖, inShell=false) → 기존 세로 스택을 그대로 인라인 렌더(폴백, 기존 모양 보존).
 *
 * SSR-safe 게이팅: 분배 게이트는 `!(inShell && slot)`. `useIsDesktop` 의 getServerSnapshot 이 true 라
 * 서버는 inShell=true 로 렌더되지만 슬롯 ref 는 클라이언트 마운트 후에만 채워진다. 슬롯이 null 인
 * 동안(SSR·hydration 첫 렌더)에는 타이틀·배너·과목 링크를 **인라인 폴백**으로 렌더해 SSR HTML 에
 * 콘텐츠가 항상 포함되게 하고(SEO/no-JS), hydration 도 일치시킨다. 슬롯이 채워지면(데스크탑) commit
 * 단계 setState 가 paint 전 동기 flush 되어 `createPortal` 로 헤더/좌/우 사이드바에 재배치한다(flash 없음).
 */

/** 가운데 대표 배너 — 랜덤 퀴즈 (데스크탑·모바일 공통, 큰 카드). */
function RandomQuizBanner() {
  return (
    <Link
      href="/theory/potato-theory-quiz"
      className="mt-6 block w-full max-w-2xl rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="m-2 md:m-0">
        <div className="flex items-center">
          <div className={bannerImageColumnClassName}>
            <Image
              src="/theory/cheer_gamja.webp"
              alt=""
              width={200}
              height={200}
              className={bannerImageClassName}
            />
          </div>
          <div className="w-1/2 p-2 md:p-4">
            <p className="text-2xl md:text-2xl font-semibold">
              정보처리기사 실기 이론
            </p>
            <p className="text-2xl md:text-4xl font-semibold mt-2">랜덤 퀴즈</p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

/* ---------------------------------------------------------------------------
   모바일/태블릿 폴백 — 기존 큰 배너(세로 스택). 기존 /theory 모양을 그대로 보존.
   --------------------------------------------------------------------------- */

function CompressedStudyBanner() {
  return (
    <Link
      href="/theory/theory-expected-questions"
      className="mt-2 block w-full max-w-2xl rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="m-2 md:m-0">
        <div className="flex items-center">
          <div className={bannerImageColumnClassName}>
            <Image
              src="/theory/moodang_gamza.webp"
              alt=""
              width={200}
              height={200}
              className={bannerImageClassName}
            />
          </div>
          <div className="w-1/2 p-2 md:p-4">
            <p className="text-2xl md:text-2xl font-semibold">
              2026년 3회 <span className="hidden md:inline">정보처리기사</span>
            </p>
            <p className="text-xl md:hidden font-semibold mt-2">정보처리기사</p>
            <p className="text-2xl md:text-4xl font-semibold mt-2">
              이론 압축 공부법
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function SummaryPdfBanner() {
  return (
    <Link
      href="/summary-pdf/26-1-compressed"
      passHref
      className="mt-2 block w-full max-w-2xl rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="m-2 md:m-0">
        <div className="flex items-center">
          <div className={bannerImageColumnClassName}>
            <Image
              src="/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp"
              alt=""
              width={200}
              height={200}
              className={bannerImageClassName}
            />
          </div>
          <div className="w-1/2 p-2 md:p-4">
            <p className="text-2xl md:text-2xl font-semibold">
              2026년 1회 <span className="hidden md:inline">정보처리기사</span>
            </p>
            <p className="text-xl md:hidden font-semibold mt-2">정보처리기사</p>
            <p className="text-2xl md:text-4xl font-semibold mt-2">
              이론 압축 요약 PDF
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function FourWeekPlanBanner() {
  return (
    <Link
      href="/theory/4week-study-plan"
      passHref
      className="mt-2 block w-full max-w-2xl rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="m-2 md:m-0">
        <div className="flex items-center">
          <div className={bannerImageColumnClassName}>
            <Image
              src="/theory/contents/4week-study-plan/detective_gamja_1.webp"
              alt=""
              width={200}
              height={227}
              className={bannerImageClassName}
            />
          </div>
          <div className="w-1/2 p-2 md:p-4">
            <p className="text-2xl md:text-2xl font-semibold">
              2026년 3회{" "}
              <span className="hidden md:inline">정보처리기사 실기</span>
            </p>
            <p className="text-xl md:hidden font-semibold mt-2">
              정보처리기사 실기
            </p>
            <p className="text-2xl md:text-4xl font-semibold mt-2">
              4주 이론공부계획
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

/* ---------------------------------------------------------------------------
   데스크탑 셸 우측 사이드바 — 컴팩트 배너(좁은 폭에 맞춘 가로 카드).
   --------------------------------------------------------------------------- */

function CompactBanner({
  href,
  image,
  badge,
  title,
}: {
  href: string;
  image: string;
  badge: string;
  title: string;
}) {
  return (
    <Link
      href={href}
      className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="transition-colors hover:border-amber-300 dark:hover:border-amber-700">
        <div className="flex items-center gap-2 p-2.5">
          <div className="flex w-16 shrink-0 items-center justify-center">
            {/* 배지 + 제목 텍스트가 링크 의미를 전달하므로 이미지는 장식(alt="") */}
            <Image
              src={image}
              alt=""
              width={64}
              height={64}
              className="h-16 w-16 object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-muted-foreground">{badge}</p>
            <p className="mt-0.5 text-base font-bold leading-snug text-foreground">
              {title}
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function RightSidebarBanners() {
  return (
    <div className="flex flex-col gap-2">
      <CompactBanner
        href="/theory/potato-theory-quiz"
        image="/theory/cheer_gamja.webp"
        badge="정보처리기사 실기 이론"
        title="랜덤 퀴즈"
      />
      <h2
        className={`mb-1 mt-2 text-lg font-semibold text-foreground ${gamjaFlower.className}`}
      >
        이렇게 공부해요
      </h2>
      <CompactBanner
        href="/theory/theory-expected-questions"
        image="/theory/moodang_gamza.webp"
        badge="2026년 3회 정보처리기사"
        title="이론 압축 공부법"
      />
      <CompactBanner
        href="/summary-pdf/26-1-compressed"
        image="/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp"
        badge="2026년 1회 정보처리기사"
        title="이론 압축 요약 PDF"
      />
      <CompactBanner
        href="/theory/4week-study-plan"
        image="/theory/contents/4week-study-plan/detective_gamja_1.webp"
        badge="2026년 3회 정보처리기사 실기"
        title="4주 이론공부계획"
      />
    </div>
  );
}

/** 상단 고정 바 타이틀 (데스크탑 셸: headerSlot 으로 portal / 폴백: 본문 상단 h1). */
const PAGE_TITLE = "정보처리기사 이론 부분 어떻게 공부해야 좋을까?";

export default function TheoryIndexContent() {
  const { inShell, headerSlot, leftSlot, rightSlot } = useIndexShell();

  return (
    <>
      {/* 데스크탑 셸 — 타이틀을 상단 고정 바(headerSlot)에 portal */}
      {inShell &&
        headerSlot &&
        createPortal(
          <div className="mx-auto flex w-full max-w-[820px] items-center justify-center px-4 py-3 sm:px-8">
            <h1
              className={`text-center text-xl font-semibold ${gamjaFlower.className}`}
            >
              {PAGE_TITLE}
            </h1>
          </div>,
          headerSlot,
        )}

      {/* 가운데(또는 폴백 본문 상단): (폴백)타이틀 + 인트로 + 랜덤 퀴즈·배너.
          게이트는 `!(inShell && slot)` — 슬롯이 채워지기 전(SSR·첫 렌더)엔 인라인 폴백을 렌더해
          SSR HTML 에 타이틀·배너·과목 링크가 항상 포함되게 한다(SEO/no-JS). 슬롯이 준비되면
          아래 portal 분기로 재배치(상호 배타). */}
      <div className="flex flex-col items-center">
        {/* 타이틀 — 셸+슬롯 준비 시 상단 고정 바로 이동, 그 전엔 본문 상단 h1 */}
        {!(inShell && headerSlot) && (
          <h1
            className={`text-center text-xl font-semibold ${gamjaFlower.className} mt-2`}
          >
            {PAGE_TITLE}
          </h1>
        )}

        <TheoryIntroClient />

        {/* 보조 배너 — 셸+슬롯 준비 시 우측 사이드바로 이동, 그 전엔 인라인 큰 배너(세로 스택) */}
        {!(inShell && rightSlot) && (
          <>
            <RandomQuizBanner />
            <CompressedStudyBanner />
            <SummaryPdfBanner />
            <FourWeekPlanBanner />
          </>
        )}
      </div>

      {/* 과목별 진입 — 셸+슬롯 준비 시 좌측 사이드바로 이동, 그 전엔 인라인 그리드 */}
      {!(inShell && leftSlot) && <CategoryButtons />}

      {/* 데스크탑 셸 — 우측 사이드바에 보조 배너 portal */}
      {inShell && rightSlot && createPortal(<RightSidebarBanners />, rightSlot)}

      {/* 데스크탑 셸 — 좌측 사이드바에 과목별 진입 portal */}
      {inShell &&
        leftSlot &&
        createPortal(<CategoryButtons variant="sidebar" />, leftSlot)}
    </>
  );
}
